import { HttpClient } from '@angular/common/http';
import { computed, effect, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { ConfigService } from '@rxap/config';
import { firstValueFrom } from 'rxjs';
import { ConfidentialityLoginDialogComponent } from './confidentiality-login-dialog/confidentiality-login-dialog.component';

export interface UserInfo {
  email: string;
  [key: string]: any;
}

export interface ConfidentialityState {
  isConfidentialMode: boolean;
  isAuthenticated: boolean;
  userInfo: UserInfo | null;
}

export const initialConfidentialityState: ConfidentialityState = {
  isConfidentialMode: false,
  isAuthenticated: false,
  userInfo: null,
};

export const confidentialityStore = signalStore(
  { providedIn: 'root' },
  withState(initialConfidentialityState),
  withComputed(({ isConfidentialMode, isAuthenticated }) => ({
    selectIsConfidentialMode: isConfidentialMode,
    selectIsAuthenticated: isAuthenticated,
    selectRequiresLoginPrompt: computed(
      () => isConfidentialMode() && !isAuthenticated()
    ),
  })),
  withMethods((store) => {
    const http = inject(HttpClient);
    const config = inject(ConfigService);
    const dialog = inject(MatDialog);
    let dialogRef: MatDialogRef<ConfidentialityLoginDialogComponent> | null = null;

    const checkSessionStatus = async () => {
      try {
        const user = await firstValueFrom(http.get<UserInfo>('/oauth2/auth'));
        patchState(store, {
          isAuthenticated: true,
          userInfo: user,
        });
      } catch (error) {
        console.error('Confidentiality Session Status Failure', error);
        patchState(store, {
          isAuthenticated: false,
          userInfo: null,
        });
      }
    };

    const requestUserInfo = async () => {
      try {
        const userInfo = await firstValueFrom(http.get<UserInfo>('/oauth2/userinfo'));
        patchState(store, { userInfo });
      } catch (error) {
        console.error('Confidentiality Request User Info Failure', error);
      }
    };

    return {
      initConfidentialCheck: () => {
        const isConfidential = !!config.get('confidential');
        patchState(store, { isConfidentialMode: isConfidential });
        if (isConfidential) {
          checkSessionStatus();
        }
      },
      setConfidentialMode: (isConfidential: boolean) => {
        patchState(store, { isConfidentialMode: isConfidential });
        if (isConfidential) {
          checkSessionStatus();
        }
      },
      checkSessionStatus,
      requestUserInfo,
      triggerLogin: () => {
        location.replace(`/oauth2/sign_in?rd=${encodeURIComponent(location.href)}`);
      },
      _initDialogEffect: () => {
        effect(() => {
          const requiresLogin = store.selectRequiresLoginPrompt();
          if (requiresLogin) {
            if (!dialogRef) {
              dialogRef = dialog.open(ConfidentialityLoginDialogComponent, {
                disableClose: true,
                autoFocus: true,
              });
            }
          } else {
            if (dialogRef) {
              dialogRef.close();
              dialogRef = null;
            }
          }
        });
      }
    };
  }),
  withHooks({
    onInit(store) {
      store.initConfidentialCheck();
      store._initDialogEffect();
    },
  })
);
